import { Lock, LogIn, Users } from 'lucide-react';
import { type FormEvent, useState } from 'react';
import { Link } from 'react-router';
import { LIMITS } from '@shared/constants/limits';
import { validateNickname } from '@shared/schemas/validation';
import type { RoomInfo } from '@shared/types/room';
import { TextField } from '@/components/TextField';
import { ApiRequestError, api } from '@/services/api';
import { saveSession } from '@/services/session';

interface JoinViewProps {
  roomId: string;
  info: RoomInfo;
  notice: string | null;
  onJoined: (session: { participantId: string; token: string }) => void;
}

export function JoinView({ roomId, info, notice, onJoined }: JoinViewProps) {
  const [nickname, setNickname] = useState('');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState<{ nickname?: string; password?: string; form?: string }>({});
  const [submitting, setSubmitting] = useState(false);
  const joinable = info.status === 'WAITING';
  const full = info.currentCount >= info.maxParticipants;

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    const vNick = validateNickname(nickname);
    if (!vNick.ok) {
      setErrors({ nickname: vNick.message });
      return;
    }
    if (info.hasPassword && password.length === 0) {
      setErrors({ password: '비밀번호를 입력해주세요.' });
      return;
    }
    setErrors({});
    setSubmitting(true);
    try {
      const result = await api.joinRoom(roomId, { nickname: vNick.value, ...(info.hasPassword ? { password } : {}) });
      saveSession(roomId, { participantId: result.participantId, token: result.token, nickname: vNick.value });
      onJoined(result);
    } catch (err) {
      if (err instanceof ApiRequestError) {
        if (err.field === 'nickname' || err.field === 'password') setErrors({ [err.field]: err.message });
        else setErrors({ form: err.message });
      } else {
        setErrors({ form: '입장하지 못했어요. 잠시 후 다시 시도해주세요.' });
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="animate-fade-up">
      <div className="card p-5 sm:p-6">
        <p className="text-xs font-semibold uppercase tracking-wide text-primary-600">초대받은 방</p>
        <h1 className="mt-1 text-2xl font-extrabold break-keep">{info.name}</h1>
        <p className="mt-2 flex items-center gap-1.5 text-sm text-charcoal-500">
          <Users size={16} aria-hidden="true" />
          현재 참여 인원 {info.currentCount} / {info.maxParticipants}명
          {info.hasPassword && (
            <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-cream-200 px-2 py-0.5 text-xs">
              <Lock size={12} aria-hidden="true" />
              비밀번호
            </span>
          )}
        </p>

        {notice && (
          <p className="mt-4 rounded-xl bg-amber-50 px-4 py-3 text-sm text-amber-800" role="status">
            {notice}
          </p>
        )}

        {!joinable ? (
          <div className="mt-6 rounded-2xl bg-cream-100 p-5 text-center">
            <p className="font-semibold">이미 게임이 시작된 방이에요.</p>
            <p className="mt-1 text-sm text-charcoal-500">새로운 방을 만들어 동료들과 다시 시작해보세요.</p>
            <Link to="/create" className="btn-primary btn-md mt-4">
              새로운 방 만들기
            </Link>
          </div>
        ) : full ? (
          <div className="mt-6 rounded-2xl bg-cream-100 p-5 text-center">
            <p className="font-semibold">방 정원이 가득 찼어요.</p>
            <p className="mt-1 text-sm text-charcoal-500">잠시 후 새로고침하거나 새 방을 만들어보세요.</p>
            <Link to="/create" className="btn-primary btn-md mt-4">
              새로운 방 만들기
            </Link>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="mt-6 flex flex-col gap-4" noValidate>
            <TextField
              id="join-nickname"
              label="닉네임을 입력해주세요"
              placeholder="예: 박과장"
              value={nickname}
              maxLength={LIMITS.NICKNAME_MAX}
              onChange={(e) => setNickname(e.target.value)}
              error={errors.nickname}
              hint={`${LIMITS.NICKNAME_MIN}~${LIMITS.NICKNAME_MAX}자 · 같은 방에서 중복 불가`}
              autoComplete="nickname"
              autoFocus
              required
            />
            {info.hasPassword && (
              <TextField
                id="join-password"
                label="방 비밀번호"
                type="password"
                value={password}
                maxLength={LIMITS.PASSWORD_MAX}
                onChange={(e) => setPassword(e.target.value)}
                error={errors.password}
                autoComplete="current-password"
                required
              />
            )}
            {errors.form && (
              <p className="rounded-xl bg-red-50 px-4 py-3 text-sm text-red-700" role="alert">
                {errors.form}
              </p>
            )}
            <button type="submit" className="btn-primary btn-lg w-full" disabled={submitting}>
              <LogIn size={20} aria-hidden="true" />
              {submitting ? '입장하는 중...' : '참여하기'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
